import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingCart, User, Menu, X, Package, LogOut, Settings } from "lucide-react";
import { useAuth, useCart } from "../App";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const Header = () => {
  const { user, login, logout } = useAuth();
  const { cart } = useCart();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const cartItemCount = cart?.items?.length || 0;

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 bg-[rgba(10,10,10,0.88)] backdrop-blur-md border-b border-red-900/30"
      data-testid="header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img
  src="/images/bt-logo.png"
  alt="BT Chilli Logo"
  className="w-20 h-20 object-contain"
/>
            <div className="hidden sm:block">
              <h1 className="text-white text-xl font-bold tracking-wide">BT Chilli</h1>
              <p className="text-[10px] text-red-300/80 tracking-[0.22em] uppercase">
                Premium Spices
              </p>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            <Link
              to="/"
              className="text-white font-semibold text-base tracking-wide hover:text-red-400 transition"
              data-testid="nav-home"
            >
              Home
            </Link>
            <Link
              to="/products"
              className="text-white font-semibold text-base tracking-wide hover:text-red-400 transition"
              data-testid="nav-products"
            >
              Products
            </Link>
            {user && (
              <Link
                to="/orders"
                 className="text-white font-semibold text-base tracking-wide hover:text-red-400 transition"
                data-testid="nav-orders"
              >
                My Orders
              </Link>
            )}
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-4">
            {/* Cart */}
            {user && (
              <Link
                to="/cart"
                className="relative p-2 hover:bg-white/5 rounded-lg transition-colors"
                data-testid="cart-icon"
              >
                <ShoppingCart className="w-5 h-5 text-gray-300" />
                {cartItemCount > 0 && (
                  <span
                    className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#d62828] text-white text-[10px] font-bold flex items-center justify-center"
                    data-testid="cart-count"
                  >
                    {cartItemCount}
                  </span>
                )}
              </Link>
            )}

            {/* User Menu */}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="flex items-center gap-2 p-2 hover:bg-white/5 rounded-lg transition-colors"
                    data-testid="user-menu-trigger"
                  >
                    {user.picture ? (
                      <img
                        src={user.picture}
                        alt={user.name}
                        className="w-8 h-8 rounded-full object-cover border border-red-900/30"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-red-900/20 flex items-center justify-center">
                        <User className="w-4 h-4 text-red-300" />
                      </div>
                    )}
                  </button>
                </DropdownMenuTrigger>

                <DropdownMenuContent
                  align="end"
                  className="w-56 bg-[#121212] border border-red-900/30 text-white"
                >
                  <div className="px-3 py-2">
                    <p className="text-sm font-medium text-white">{user.name}</p>
                    <p className="text-xs text-gray-400">{user.email}</p>
                  </div>

                  <DropdownMenuSeparator className="bg-red-900/20" />

                  <DropdownMenuItem
                    onClick={() => navigate("/profile")}
                    data-testid="menu-profile"
                    className="text-gray-200 focus:bg-white/5 focus:text-white"
                  >
                    <User className="w-4 h-4 mr-2" />
                    Profile
                  </DropdownMenuItem>

                  <DropdownMenuItem
                    onClick={() => navigate("/orders")}
                    data-testid="menu-orders"
                    className="text-gray-200 focus:bg-white/5 focus:text-white"
                  >
                    <Package className="w-4 h-4 mr-2" />
                    My Orders
                  </DropdownMenuItem>

                  {user.role === "admin" && (
                    <>
                      <DropdownMenuSeparator className="bg-red-900/20" />
                      <DropdownMenuItem
                        onClick={() => navigate("/admin")}
                        data-testid="menu-admin"
                        className="text-gray-200 focus:bg-white/5 focus:text-white"
                      >
                        <Settings className="w-4 h-4 mr-2" />
                        Admin Dashboard
                      </DropdownMenuItem>
                    </>
                  )}

                  <DropdownMenuSeparator className="bg-red-900/20" />

                  <DropdownMenuItem
                    onClick={logout}
                    className="text-red-400 focus:bg-red-950/30 focus:text-red-300"
                    data-testid="menu-logout"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button
                onClick={login}
                className="bg-[#a30d18] hover:bg-[#7f0a12] text-white rounded-full px-6"
                data-testid="login-btn"
              >
                Sign In
              </Button>
            )}

            {/* Mobile Menu Toggle */}
            <button
              className="md:hidden p-2 hover:bg-white/5 rounded-lg text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div
          className="md:hidden bg-[rgba(10,10,10,0.96)] backdrop-blur-md border-t border-red-900/30"
          data-testid="mobile-menu"
        >
          <nav className="px-4 py-4 space-y-2">
            <Link
              to="/"
              className="text-white font-semibold tracking-wide hover:text-red-400 transition"
              onClick={() => setMobileMenuOpen(false)}
            >
              Home
            </Link>
            <Link
              to="/products"
              className="text-white font-semibold tracking-wide hover:text-red-400 transition"
              onClick={() => setMobileMenuOpen(false)}
            >
              Products
            </Link>
            {user && (
              <Link
                to="/orders"
                className="text-white font-semibold tracking-wide hover:text-red-400 transition"
                onClick={() => setMobileMenuOpen(false)}
              >
                My Orders
              </Link>
            )}
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;